import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Bell,
  Check,
  Download,
  KeyRound,
  MonitorDown,
  MoreVertical,
  PlusSquare,
  Share,
  Smartphone,
} from 'lucide-react';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { useInstallPrompt } from '../hooks';
import { isAppleMobile, isInstalledApp } from '../lib/pwa';
import { useAtomValue } from 'jotai';
import { currentUserAtom } from '../store';
import { useT } from '../i18n';
import { fillParts } from '../components/page/fillParts';
import { StoreBadges } from '../components/site/StoreBadges';
import { useStoreLinks } from '../lib/siteConfig';
import { hasAnyBadge, storeChoice, type StoreDevice } from '../lib/storeLinks';

// Step-by-step: putting Twinden on the home screen and turning on
// notifications, for the device it's opened on (docs/new-devices.md, D141).

type Platform = 'installed' | 'ios' | 'android' | 'computer';

export function detectPlatform(
  userAgent = navigator.userAgent,
  installed = isInstalledApp(),
  appleMobile = isAppleMobile()
): Platform {
  if (installed) return 'installed';
  if (appleMobile) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'computer';
}

/** iPhone browsers other than Safari (Chrome, Firefox, Edge on iOS). */
function notSafariOnIphone(userAgent = navigator.userAgent) {
  return /CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
}

function Step({
  n,
  icon,
  children,
}: {
  n: number;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <HStack align="flex-start" spacing={3}>
      <Flex
        w="32px"
        h="32px"
        flexShrink={0}
        borderRadius="full"
        bg="accent.solid"
        color="accent.onSolid"
        align="center"
        justify="center"
        fontWeight={700}
      >
        {n}
      </Flex>
      <Box flex="1" minW={0} pt="4px">
        <Text fontSize="16px" lineHeight="23px" color="ink.body">
          {children}
        </Text>
      </Box>
      <Flex
        w="40px"
        h="40px"
        flexShrink={0}
        borderRadius="12px"
        bg="surface.subtle"
        color="accent"
        align="center"
        justify="center"
        aria-hidden="true"
      >
        {icon}
      </Flex>
    </HStack>
  );
}

function Steps({ children }: { children: ReactNode }) {
  return (
    <VStack
      align="stretch"
      spacing={5}
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={5}
    >
      {children}
    </VStack>
  );
}

function NotificationsStep({ n, signedIn }: { n: number; signedIn: boolean }) {
  const t = useT();
  return (
    <Step n={n} icon={<Bell size={20} />}>
      {fillParts(t('account.install.notifications'), {
        path: (
          <strong>
            {t('account.menu.settings')} → {t('account.settings.notifications')}{' '}
            → {t('account.settings.turnOn')}
          </strong>
        ),
        // The browser's own button.
        allow: <strong>Allow</strong>,
      })}{' '}
      {signedIn && (
        <Link as={RouterLink} to="/settings" color="accent" fontWeight={600}>
          {t('account.install.openSettings')}
        </Link>
      )}
    </Step>
  );
}

/**
 * The store app first, once this device's store link is set (D346): the
 * badge for a phone, both for a computer with "works best on your phone".
 */
function GetTheApp({ device }: { device: StoreDevice }) {
  const t = useT();
  const phone = device !== 'computer';
  return (
    <VStack
      align="stretch"
      spacing={3}
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={5}
    >
      <HStack spacing={3}>
        <Flex
          w="40px"
          h="40px"
          flexShrink={0}
          borderRadius="12px"
          bg="accent.tint"
          color="accent"
          align="center"
          justify="center"
          aria-hidden="true"
        >
          <Smartphone size={20} />
        </Flex>
        <Text as="h2" fontSize="18px" fontWeight={800}>
          {t('account.install.getApp')}
        </Text>
      </HStack>
      {phone && (
        <Text fontSize="16px" lineHeight="23px" color="ink.body">
          {t('account.install.getAppBody')}
        </Text>
      )}
      <StoreBadges device={device} />
    </VStack>
  );
}

export function InstallGuidePage() {
  const platform = detectPlatform();
  const signedIn = useAtomValue(currentUserAtom) !== null;
  const install = useInstallPrompt();
  const t = useT();
  const storeLinks = useStoreLinks();
  const device: StoreDevice =
    platform === 'ios'
      ? 'ios'
      : platform === 'android'
        ? 'android'
        : 'computer';
  const showApp =
    platform !== 'installed' && hasAnyBadge(storeChoice(storeLinks, device));
  // On a phone the store app comes first, and the web app is the other way.
  const orWebApp = showApp && device !== 'computer' && (
    <Text fontSize="16px" fontWeight={700} color="muted" pt={2}>
      {t('account.install.orWebApp')}
    </Text>
  );
  const installButton = (
    <Button size="sm" h="32px" onClick={() => void install.prompt()}>
      {t('account.settings.install')}
    </Button>
  );
  const fromIcon = <strong>{t('account.install.fromIcon')}</strong>;

  return (
    <WelcomeShell
      title={t('account.settings.install')}
      subtitle={t('account.install.subtitle')}
      footer={
        <Link as={RouterLink} to={signedIn ? '/' : '/login'} color="accent">
          {t('account.install.back')}
        </Link>
      }
    >
      <VStack align="stretch" spacing={4}>
        {showApp && <GetTheApp device={device} />}
        {orWebApp}
        {platform === 'installed' && (
          <>
            <HStack
              bg="accent.tint"
              borderRadius="18px"
              px={4}
              py={3}
              spacing={3}
            >
              <Check size={20} />
              <Text fontWeight={700}>{t('account.install.installed')}</Text>
            </HStack>
            <Steps>
              <NotificationsStep n={1} signedIn={signedIn} />
            </Steps>
          </>
        )}

        {platform === 'ios' && (
          <Steps>
            {notSafariOnIphone() && (
              <Text fontWeight={700} color="accent">
                {t('account.install.safariFirst')}
              </Text>
            )}
            <Step n={1} icon={<Share size={20} />}>
              {fillParts(t('account.install.ios1'), {
                // The iPhone's own button names stay as the phone shows them.
                share: <strong>Share</strong>,
              })}
            </Step>
            <Step n={2} icon={<PlusSquare size={20} />}>
              {fillParts(t('account.install.ios2'), {
                add: <strong>Add to Home Screen</strong>,
                confirm: <strong>Add</strong>,
              })}
            </Step>
            <Step n={3} icon={<Smartphone size={20} />}>
              {fillParts(t('account.install.ios3'), { fromIcon })}
            </Step>
            <Step n={4} icon={<KeyRound size={20} />}>
              {t('account.install.ios4')}
              {signedIn && (
                <>
                  {' '}
                  {fillParts(t('account.install.ios4Keys'), {
                    keepOpen: <strong>{t('account.install.keepOpen')}</strong>,
                  })}
                </>
              )}
            </Step>
            <NotificationsStep n={5} signedIn={false} />
          </Steps>
        )}

        {platform === 'android' && (
          <Steps>
            {install.available ? (
              <Step n={1} icon={<Download size={20} />}>
                {fillParts(t('account.install.tapInstall'), {
                  button: installButton,
                })}
              </Step>
            ) : (
              <Step n={1} icon={<MoreVertical size={20} />}>
                {fillParts(t('account.install.android1'), {
                  // Chrome's own menu items.
                  menu: <strong>⋮</strong>,
                  installApp: <strong>Install app</strong>,
                  addHome: <strong>Add to Home screen</strong>,
                })}
              </Step>
            )}
            <Step n={2} icon={<Smartphone size={20} />}>
              {fillParts(t('account.install.android2'), { fromIcon })}
            </Step>
            <NotificationsStep n={3} signedIn={signedIn} />
          </Steps>
        )}

        {platform === 'computer' && (
          <Steps>
            {install.available ? (
              <Step n={1} icon={<Download size={20} />}>
                {fillParts(t('account.install.clickInstall'), {
                  button: installButton,
                })}
              </Step>
            ) : (
              <Step n={1} icon={<MonitorDown size={20} />}>
                {fillParts(t('account.install.computer1'), {
                  icon: <strong>{t('account.install.installIcon')}</strong>,
                  // The Mac's own menu.
                  dock: <strong>File → Add to Dock</strong>,
                })}
              </Step>
            )}
            <NotificationsStep n={2} signedIn={signedIn} />
          </Steps>
        )}
      </VStack>
    </WelcomeShell>
  );
}
